// src/pages/EditRepairOrdersPage.js
import React, { useMemo, useState, useEffect } from "react";
import { useROStore } from "../context/ROContext";
import { RO_STATUS } from "../constants/roStatus";
import { noteTextFromValue } from "../utils/roMessageLegacy";

function safeDate(input) {
  if (!input) return null;
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatWhen(iso) {
  const d = safeDate(iso);
  if (!d) return "—";
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  })}`;
}

function normalize(text) {
  return String(text ?? "").trim().toLowerCase();
}

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

export default function EditRepairOrdersPage() {
  const store = useROStore();

  const ros = useMemo(() => {
    const a = Array.isArray(store?.repairOrders) ? store.repairOrders : null;
    const b = Array.isArray(store?.ros) ? store.ros : null;
    return a || b || [];
  }, [store?.repairOrders, store?.ros]);

  const currentUser = store?.currentUser;
  const currentUserName = useMemo(() => {
    return String(currentUser?.name ?? currentUser ?? "Advisor").trim() || "Advisor";
  }, [currentUser]);

  const updateRO = store?.updateRO;
  const deleteRO = store?.deleteRO;

  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const [banner, setBanner] = useState(null);
  const [saving, setSaving] = useState(false);

  const results = useMemo(() => {
    const q = normalize(search);
    if (!q) return ros;

    return ros.filter((r) => {
      const roNum = normalize(r?.roNumber);
      const tag = normalize(r?.tagNumber);
      const cust = normalize(r?.customerName);
      const veh = normalize(r?.vehicle);
      const job = normalize(r?.jobType ?? r?.concernType ?? r?.concern);
      const adv = normalize(r?.advisorName);
      return (
        roNum.includes(q) ||
        tag.includes(q) ||
        cust.includes(q) ||
        veh.includes(q) ||
        job.includes(q) ||
        adv.includes(q)
      );
    });
  }, [ros, search]);

  const selectedRO = useMemo(() => {
    if (!selectedId) return null;
    return ros.find((r) => String(r?.id) === String(selectedId)) || null;
  }, [ros, selectedId]);

  const [formTagNumber, setFormTagNumber] = useState("");
  const [formCustomerName, setFormCustomerName] = useState("");
  const [formVehicle, setFormVehicle] = useState("");

  useEffect(() => {
    setBanner(null);
    if (!selectedRO) return;
    setFormTagNumber(safeString(selectedRO.tagNumber).trim());
    setFormCustomerName(selectedRO.customerName || "");
    setFormVehicle(selectedRO.vehicle || "");
  }, [selectedRO]);

  const readOnlyNotesText = useMemo(() => {
    return noteTextFromValue(selectedRO?.notes);
  }, [selectedRO?.notes]);

  function validateTagForEdit(nextTag) {
    const tag = safeString(nextTag).trim();
    if (!tag) return { ok: false, message: "Tag # is required." };

    if (typeof store?.validateTagNumber === "function" && selectedRO?.id) {
      const res = store.validateTagNumber(tag, selectedRO.id);
      return res?.ok
        ? { ok: true }
        : { ok: false, message: res?.message || "Tag # is already in use on an OPEN RO." };
    }

    const tagNorm = tag.toUpperCase();
    const dup = ros.some((r) => {
      if (String(r?.id) === String(selectedRO?.id)) return false;
      const t = safeString(r?.tagNumber).trim().toUpperCase();
      if (!t) return false;

      const st = String(r?.status || "").toUpperCase();
      const lc = String(r?.lifecycle || "").toUpperCase();
      const completed = st === "COMPLETED" || lc === "COMPLETED";
      const archived = st === "ARCHIVED" || lc === "ARCHIVED";
      if (completed || archived) return false;

      return t === tagNorm;
    });

    if (dup) return { ok: false, message: "Tag # is already in use on an OPEN RO." };
    return { ok: true };
  }

  async function updateROCompat(id, patch, eventType, meta) {
    if (typeof updateRO !== "function") throw new Error("updateRO is missing in ROContext.js");

    try {
      const res = updateRO(id, patch, eventType, meta);
      if (res && typeof res.then === "function") await res;
      return;
    } catch {}

    try {
      const res = updateRO(id, patch);
      if (res && typeof res.then === "function") await res;
      return;
    } catch {}

    const res = updateRO(id, (prev) => ({ ...(prev || {}), ...(patch || {}) }));
    if (res && typeof res.then === "function") await res;
  }

  async function saveChanges() {
    if (!selectedRO) return;
    if (saving) return;

    setBanner(null);

    const nextTag = safeString(formTagNumber).trim();
    const nextCustomer = safeString(formCustomerName).trim();
    const nextVehicle = safeString(formVehicle).trim();

    const tagRes = validateTagForEdit(nextTag);
    if (!tagRes.ok) {
      setBanner({ type: "error", message: tagRes.message });
      return;
    }

    try {
      setSaving(true);

      const patch = {
        tagNumber: nextTag,
        customerName: nextCustomer,
        vehicle: nextVehicle,
        updatedAt: new Date().toISOString(),
      };

      await updateROCompat(selectedRO.id, patch, "RO_EDITED", {
        byName: currentUserName,
        fields: ["tagNumber", "customerName", "vehicle"],
      });

      setBanner({ type: "success", message: `Saved ✓ RO #${selectedRO.roNumber}` });
    } catch (e) {
      setBanner({ type: "error", message: String(e?.message || e) });
    } finally {
      setSaving(false);
    }
  }

  async function markCompleted() {
    if (!selectedRO) return;

    try {
      if (typeof store?.setStatus === "function") {
        store.setStatus(selectedRO.id, RO_STATUS.COMPLETED);
        setBanner({ type: "success", message: `Marked completed ✓ RO #${selectedRO.roNumber}` });
        return;
      }

      const patch = {
        status: RO_STATUS.COMPLETED,
        lifecycle: "COMPLETED",
        completedAtISO: selectedRO.completedAtISO || new Date().toISOString(),
        timestamps: {
          ...(selectedRO.timestamps || {}),
          completedAt: selectedRO.timestamps?.completedAt || new Date().toISOString(),
        },
        updatedAt: new Date().toISOString(),
      };

      await updateROCompat(selectedRO.id, patch, "RO_STATUS_CHANGED", { to: "COMPLETED" });

      setBanner({ type: "success", message: `Marked completed ✓ RO #${selectedRO.roNumber}` });
    } catch (e) {
      setBanner({ type: "error", message: String(e?.message || e) });
    }
  }

  function removeRO() {
    if (!selectedRO) return;
    if (typeof deleteRO !== "function") {
      setBanner({ type: "error", message: "void/delete action is missing in ROContext.js" });
      return;
    }
    const ok = window.confirm(`Void RO #${selectedRO.roNumber}? This removes it from active workflow but preserves it in history/audit records.`);
    if (!ok) return;

    deleteRO(selectedRO.id);
    setSelectedId(null);
    setBanner({ type: "success", message: `Voided ✓ RO #${selectedRO.roNumber}` });
  }

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>Edit Repair Orders</h2>

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search RO #, tag #, customer, vehicle, job..."
          style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid #ddd", minWidth: 340 }}
        />

        <div style={{ marginLeft: "auto", fontSize: 13, opacity: 0.75 }}>
          Logged in as: <b>{currentUserName}</b> • Results: <b>{results.length}</b>
        </div>
      </div>

      {banner ? (
        <div
          style={{
            marginTop: 12,
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid #e5e7eb",
            fontWeight: 900,
            background:
              banner.type === "success"
                ? "#ecfdf5"
                : banner.type === "error"
                ? "#fff1f2"
                : "#f8fafc",
            color:
              banner.type === "success"
                ? "#065f46"
                : banner.type === "error"
                ? "#991b1b"
                : "#0f172a",
          }}
        >
          {banner.message}
        </div>
      ) : null}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
        <div style={{ border: "1px solid #e6e6e6", borderRadius: 14, background: "white", padding: 12 }}>
          <div style={{ fontWeight: 900, marginBottom: 8 }}>Repair Orders</div>

          {results.length === 0 ? (
            <div style={{ padding: 12, border: "1px dashed #ccc", borderRadius: 12, opacity: 0.8 }}>
              No repair orders match this search.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {results.slice(0, 80).map((ro) => {
                const active = String(ro?.id) === String(selectedId);
                const tag = safeString(ro?.tagNumber).trim();
                return (
                  <button
                    key={ro?.id || ro?.roNumber}
                    onClick={() => setSelectedId(ro?.id || null)}
                    style={{
                      textAlign: "left",
                      border: "1px solid #e5e7eb",
                      borderRadius: 12,
                      padding: 10,
                      background: active ? "#f3f4f6" : "white",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontWeight: 900 }}>
                      RO #{ro?.roNumber || "—"}{" "}
                      {tag ? <span style={{ fontWeight: 900, marginLeft: 8, opacity: 0.85 }}>• Tag #{tag}</span> : null}
                      <span style={{ fontWeight: 700, opacity: 0.7, marginLeft: 8 }}>
                        • {String(ro?.status || "open").toUpperCase()}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.85 }}>
                      <b>{ro?.customerName || "—"}</b> — {ro?.vehicle || "—"}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>
                      Created: {formatWhen(ro?.timestamps?.createdAt || ro?.createdAt)} • Updated:{" "}
                      {formatWhen(ro?.updatedAt || ro?.timestamps?.updatedAt)}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ border: "1px solid #e6e6e6", borderRadius: 14, background: "white", padding: 12 }}>
          <div style={{ fontWeight: 900, marginBottom: 8 }}>Editor</div>

          {!selectedRO ? (
            <div style={{ padding: 12, border: "1px dashed #ccc", borderRadius: 12, opacity: 0.8 }}>
              Select an RO on the left to edit.
            </div>
          ) : (
            <>
              <div style={{ fontWeight: 900, fontSize: 16 }}>RO #{selectedRO.roNumber}</div>

              <div style={{ fontSize: 13, opacity: 0.75, marginTop: 4 }}>
                Tag #: <b>{safeString(selectedRO.tagNumber).trim() || "—"}</b> • Advisor:{" "}
                <b>{selectedRO.advisorName || "—"}</b>
              </div>

              <div style={{ fontSize: 13, opacity: 0.75, marginTop: 4 }}>
                Created: <b>{formatWhen(selectedRO.timestamps?.createdAt || selectedRO.createdAt)}</b>
              </div>

              <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                  Tag # (Key Tag)
                  <input
                    value={formTagNumber}
                    onChange={(e) => setFormTagNumber(e.target.value)}
                    style={{ width: "100%", marginTop: 6, padding: "10px 12px", borderRadius: 10, border: "1px solid #ddd" }}
                    placeholder="e.g., 421"
                  />
                </label>

                <label style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                  Customer Name
                  <input
                    value={formCustomerName}
                    onChange={(e) => setFormCustomerName(e.target.value)}
                    style={{ width: "100%", marginTop: 6, padding: "10px 12px", borderRadius: 10, border: "1px solid #ddd" }}
                  />
                </label>

                <label style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                  Vehicle
                  <input
                    value={formVehicle}
                    onChange={(e) => setFormVehicle(e.target.value)}
                    style={{ width: "100%", marginTop: 6, padding: "10px 12px", borderRadius: 10, border: "1px solid #ddd" }}
                  />
                </label>

                <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                  Notes & Messages
                  <div
                    style={{
                      width: "100%",
                      minHeight: 110,
                      marginTop: 6,
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid #ddd",
                      background: "#f8fafc",
                      color: "#374151",
                      fontWeight: 500,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {readOnlyNotesText || "No notes or messages yet."}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: "#64748b" }}>
                    Add new notes from the RO Thread.
                  </div>
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button
                    onClick={saveChanges}
                    disabled={saving}
                    style={{
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid #ddd",
                      background: "white",
                      cursor: "pointer",
                      fontWeight: 800,
                      opacity: saving ? 0.6 : 1,
                    }}
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>

                  <button
                    onClick={markCompleted}
                    style={{
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid #ddd",
                      background: "white",
                      cursor: "pointer",
                      fontWeight: 800,
                    }}
                  >
                    Mark Completed
                  </button>

                  <button
                    onClick={removeRO}
                    style={{
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid #ddd",
                      background: "white",
                      cursor: "pointer",
                      fontWeight: 800,
                    }}
                  >
                    Void RO
                  </button>
                </div>

                <div style={{ fontSize: 12, opacity: 0.7 }}>
                  Tip: Tag # follows the RO everywhere as long as it is stored on the RO record.
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
